// Shared hazard ids and reusable trigger questions — the same curated hazards
// and reviewer-evidence questions appear across many reports. Reuse these in
// every report's trigger set rather than re-authoring them.

import type { TriggerQuestion } from "./types";

// Canonical shared hazard ids (must match the hazardId in each report JSON).
export const ASBESTOS = "haz-asbestos-asbesto-disturbance-or-contact";
export const OCCUPIED = "haz-public-interface-public-interface-occupied-premise-and-occupant-disruption";
export const WAH = "haz-working-at-height-fall-from-height-during-installation-or-work-at-height";
export const OVERHEAD = "haz-electrical-contact-contact-with-overhead-electric-line-or-cable";
export const SILICA = "haz-dust-fumes-silica-and-construction-dust-inhalation";
export const FALLING_OBJECTS = "haz-working-at-height-falling-object-from-working-at-height";
export const FALLING_MATERIAL_PUBLIC = "haz-public-interface-falling-material-or-object-striking-person-below";
export const MANUAL_HANDLING = "haz-manual-handling-manual-handling-heavy-or-bulky-item";
export const HAVS = "haz-noise-vibration-hand-arm-vibration-syndrome-hav";
export const PLANT_PEDESTRIAN = "haz-plant-equipment-plant-and-vehicle-collision-with-pedestrian-or-worker";
export const CONFINED_SPACE = "haz-confined-space-confined-space-atmosphere-hazard";
export const SLIPS_TRIPS = "haz-slips-trips-falls-slip-trip-and-fall-at-same-level";
export const FRAGILE_ROOF = "haz-working-at-height-fall-through-fragile-or-weakened-roof-surface";
export const UNDERGROUND_SERVICES = "haz-excavation-contact-with-underground-service-during-excavation";
export const LADDER_INSTABILITY = "haz-working-at-height-ladder-instability-or-unsafe-acces-equipment";

export const ACCESS_OPTIONS = ["Mobile tower (PASMA)", "Fixed scaffold", "Ladder / roof ladder", "MEWP"];

/** Evidence reviewers look for: who issues the hot work permit and its validity. */
export const HOT_WORK_PERMIT_Q: TriggerQuestion = {
  key: "hotWorkPermit",
  label: "Hot work permit — issuer and validity",
  kind: "text",
  placeholder: "e.g. issued by PC site office, valid day of works only",
  requiredCheck: "Hot work permit issuer/validity recorded",
  effects: { "*": { scopeLine: "Hot work permit: {answer}. Fire watch maintained for 60 minutes after hot work ceases." } },
};

/** Structured COSHH detail — named substances and SDS references replace the
 *  generic "See COSHH assessment" line in the Substances section. */
export const COSHH_DETAIL_QS: TriggerQuestion[] = [
  {
    key: "substancesUsed",
    label: "Which substances will be used?",
    kind: "text",
    placeholder: "e.g. Powerflux, LSF cleaner, Fernox F1 inhibitor",
    requiredCheck: "Substances named for COSHH",
    effects: { "*": { substancesAdd: "In use: {answer}" } },
  },
  {
    key: "sdsRefs",
    label: "SDS held — date / version",
    kind: "text",
    placeholder: "e.g. Powerflux SDS v3 (2024); F1 SDS (2023)",
    requiredCheck: "SDS references recorded",
    effects: { "*": { substancesAdd: "SDS held: {answer}. Exposure routes, storage, spill response and disposal per the SDS and the COSHH controls in this RAMS." } },
  },
];

export const RESCUE_Q: TriggerQuestion = {
  key: "rescuePlan",
  label: "Is a rescue plan in place for work at height?",
  kind: "yesno",
  effects: {
    yes: { scopeLine: "A work-at-height rescue plan is in place before work starts." },
    no: { failCheck: "Work-at-height rescue plan confirmed" },
  },
  notSure: { failCheck: "Work-at-height rescue plan confirmed" },
  followUps: {
    when: "yes",
    questions: [
      {
        key: "rescueOwner",
        label: "Who owns the rescue plan (name / role)?",
        kind: "text",
        placeholder: "e.g. J Smith, site supervisor",
        requiredCheck: "Rescue plan owner named",
        effects: { "*": { scopeLine: "Rescue plan owner: {answer}." } },
      },
    ],
  },
};

export const PRE2000_Q = (hazOn: string[], hazOff: string[]): TriggerQuestion => ({
  key: "pre2000",
  label: "Is the building pre-2000, or is asbestos information unavailable?",
  kind: "yesno",
  effects: {
    yes: { hazardsOn: hazOn, scopeLine: "Pre-2000 building or no asbestos information — asbestos precautions apply; check the asbestos register before intrusive work." },
    // "Not applicable + reason" beats silent omission — a RAMS with no asbestos
    // line on a refurb job is itself a rejection trigger.
    no: { hazardsOff: hazOff, emergencyOff: ["asbestos"], scopeLine: "Building constructed after 2000 — asbestos-containing materials are not reasonably foreseeable; asbestos precautions are not required for this task." },
  },
  notSure: {
    hazardsOn: hazOn,
    scopeLine: "Asbestos status unconfirmed — treat suspect materials as ACM; obtain the asbestos register before intrusive work.",
    failCheck: "Asbestos information confirmed",
  },
});
